import { getField, updateField } from 'vuex-map-fields'
import api from '@/lib/api'
import { IN_PROGRESS, COMPLETE, ERROR } from '@/lib/constants/actionStatuses'
import { PATH_ONE_OFF_EXPENSES_RECORDS } from '@/lib/constants/api'
import updateStatus from '@/store/helpers/updateStatus'
import { notifyError, notifySuccess } from '@/lib/notifications'
import buildInitialState from '@/store/helpers/buildInitialState'
import { hideProgressModal, showProgressModal } from '@/lib/progressModal'
import getOneOffFeeRecord from '@/store/modules/models/oneOffFeeRecord'

const oneOffFeesRecords = {
  namespaced: true,
  state: buildInitialState({
    oneOffFeesRecordsList: [],
    ...getOneOffFeeRecord()
  }),
  getters: {
    getField
  },
  mutations: {
    updateField,
    updateStatus,
    setOneOffFeeRecordsList: (state, oneOffFeesRecordsList) => {
      state.oneOffFeesRecordsList = oneOffFeesRecordsList
    },
    setOneOffFeeRecord (state, oneOffFeeRecord) {
      state.id = oneOffFeeRecord.id
      state.assignmentId = oneOffFeeRecord.assignmentId
      state.description = oneOffFeeRecord.description
      state.payAmount = oneOffFeeRecord.payAmount
      state.billAmount = oneOffFeeRecord.billAmount
      state.date = oneOffFeeRecord.date
      state.notes = oneOffFeeRecord.notes
      state.createdBy = oneOffFeeRecord.createdBy
      state.updatedBy = oneOffFeeRecord.updatedBy
    },
    clearOneOffFeeRecord (state) {
      state.id = null
      state.assignmentId = null
      state.description = null
      state.payAmount = null
      state.billAmount = null
      state.date = null
      state.notes = null
      state.createdBy = null
      state.updatedBy = null
    }
  },
  actions: {
    getOneOffFeesRecords: ({ commit, dispatch }, filters = {}) => {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Getting one off fees records, please wait...' })

      return api.doGet({ path: PATH_ONE_OFF_EXPENSES_RECORDS, parameters: filters })
        .then(json => json.data)
        .then(oneOffFeeRecordsList => {
          commit('updateStatus', { status: COMPLETE })
          commit('setOneOffFeeRecordsList', oneOffFeeRecordsList)
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to select one off fees records')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    getOneOffFeeRecord ({ commit, dispatch }, id = null) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Getting one off fee record, please wait...' })
      return api.doGet({ path: PATH_ONE_OFF_EXPENSES_RECORDS, parameters: Object.assign({}, { id: id }) })
        .then(json => json.data)
        .then(oneOffFeeRecord => {
          commit('updateStatus', { status: COMPLETE })
          commit('setOneOffFeeRecord', oneOffFeeRecord)
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to select one off fee record')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    saveOneOffFee ({ commit, dispatch }, oneOffFeeRecord) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Saving one off fee record, please wait...' })
      return api.doPost({ path: PATH_ONE_OFF_EXPENSES_RECORDS, data: oneOffFeeRecord })
        .then(response => {
          commit('updateStatus', { status: COMPLETE })

          if (!response.data) {
            throw new Error('Unable to save one off fee record')
          }

          commit('clearOneOffFeeRecord')
          notifySuccess(dispatch, 'One off fee record saved')
          return response.data
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to save one off fee record')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    updateOneOffFee ({ commit, dispatch }, oneOffFeeRecord) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Updating one off fee record, please wait...' })
      return api.doPut({ path: PATH_ONE_OFF_EXPENSES_RECORDS, data: oneOffFeeRecord })
        .then(response => {
          commit('updateStatus', { status: COMPLETE })

          if (!response.data) {
            throw new Error('Unable to update one off fee record')
          }

          commit('clearOneOffFeeRecord')
          notifySuccess(dispatch, 'One off fee record updated')
          return response.data
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to update one off fee record')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    },
    deleteOneOffFee ({ commit, dispatch }, id = null) {
      commit('updateStatus', { status: IN_PROGRESS })
      showProgressModal({ dispatch, message: 'Deleting one off fee record, please wait...' })
      return api.doDelete({
        path: PATH_ONE_OFF_EXPENSES_RECORDS,
        parameters: Object.assign({}, { id: id })
      })
        .then(response => {
          commit('updateStatus', { status: COMPLETE })

          if (!response.data) {
            throw new Error('Unable to delete one off fee record')
          }

          commit('clearOneOffFeeRecord')
          notifySuccess(dispatch, 'One off fee record deleted')
          return response.data
        })
        .catch((error) => {
          commit('updateStatus', { status: ERROR, error })
          notifyError(dispatch, 'Unable to delete one off fee record')
        })
        .finally(() => {
          hideProgressModal({ dispatch })
        })
    }
  }
}

export default oneOffFeesRecords
